/**
 * Gera a URL pública de um identificador de cardápio.
 *
 *  - MESA/COMANDA: QR no celular do cliente — abre em `/checkout/<uuid>`. O
 *    backend resolve o tenant pelo UUID no path (efêmero, não instala PWA).
 *  - TOTEM/TABLET: dispositivos fixos (kiosk) — usam SUBDOMÍNIO com o slug
 *    (`codigo`), igual delivery/vitrine. Assim o app instalado (PWA) abre com o
 *    tenant já resolvido pelo host, sem depender do `/checkout` na URL. Totens
 *    LEGADOS (código ainda em UUID) caem no `/checkout/<uuid>` — o aparelho já
 *    configurado continua funcionando.
 *  - DELIVERY/VITRINE: subdomínio com o slug, ou domínio completo do cliente.
 *
 * MULTI-HOST: a plataforma responde em vários domínios (ex.: `cardapio.sischef.com`
 * e `sischef.shop`) e o mesmo `codigo` resolve em qualquer um deles (o backend
 * casa o subdomínio com o `identificador_cardapio.codigo`, agnóstico de domínio).
 * A lista vem da env `VITE_DOMINIOS_CARDAPIO` (CSV; o 1º é o CANÔNICO, usado por
 * padrão ao exibir links). Em dev, deriva do `window.location` atual.
 */

export type TipoIdent = 'MESA' | 'COMANDA' | 'TOTEM' | 'TOTEM_CHECKOUT' | 'TABLET' | 'DELIVERY' | 'VITRINE';

interface IdentParaUrl {
  tipo: TipoIdent;
  codigo: string;
  /** DELIVERY/VITRINE: se true, `codigo` já é o domínio completo (cardapiox.com.br). */
  dominioCompleto?: boolean;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Domínios de plataforma suportados, do CANÔNICO (1º) para os demais. Aceita a
 * env nova `VITE_DOMINIOS_CARDAPIO` (CSV) e mantém compatibilidade com a antiga
 * `VITE_DOMINIO_CARDAPIO` (single). Default: os dois domínios oficiais.
 */
export const DOMINIOS_CARDAPIO: string[] = (() => {
  const lista = (import.meta.env.VITE_DOMINIOS_CARDAPIO as string | undefined) ?? '';
  const legado = (import.meta.env.VITE_DOMINIO_CARDAPIO as string | undefined) ?? '';
  const bruto = lista || legado || 'sischef.shop,cardapio.sischef.com';
  const dominios = bruto
    .split(',')
    .map((d) => d.trim().toLowerCase())
    .filter(Boolean);
  return dominios.length > 0 ? dominios : ['sischef.shop'];
})();

/** Domínio canônico da plataforma (usado por padrão ao exibir/gerar links). */
export function dominioCanonico(): string {
  return DOMINIOS_CARDAPIO[0]!;
}

export function urlPareamento(): string {
  if (import.meta.env.DEV) return `${window.location.origin}/totem/parear`;
  return `https://${dominioCanonico()}/totem/parear`;
}

/** MESA/COMANDA são QR (path /checkout); os demais tipos usam subdomínio/domínio. */
function ehCheckoutPath(tipo: TipoIdent): boolean {
  return tipo === 'MESA' || tipo === 'COMANDA';
}

function hostEhIp(host: string): boolean {
  const semPorta = host.split(':')[0] ?? '';
  return /^\d{1,3}(\.\d{1,3}){3}$/.test(semPorta) || semPorta.startsWith('[');
}

/**
 * Monta a URL pública do identificador. `dominio` sobrescreve o canônico (ex.:
 * para exibir o mesmo ponto de acesso em `sischef.shop`); ignorado em dev, no
 * domínio completo do cliente e no path /checkout.
 */
export function montarUrlIdentificador(ident: IdentParaUrl, dominio?: string): string {
  const ehDev = import.meta.env.DEV;

  // Domínio próprio (delivery/vitrine): o código já é o host completo.
  if (!ehCheckoutPath(ident.tipo) && ident.dominioCompleto) {
    return `https://${ident.codigo}`;
  }

  // Totem/tablet legados (código ainda em UUID) seguem no /checkout — o aparelho
  // já configurado não deve mudar de endereço. Novos usam slug → subdomínio.
  const ehSubdominio = !ehCheckoutPath(ident.tipo) && !UUID_RE.test(ident.codigo);

  if (ehDev) {
    const { protocol, host } = window.location; // ex.: "http:" e "localhost:5173"
    // Subdomínio só quando o host aceita (IP não aceita → usa path).
    return ehSubdominio && !hostEhIp(host)
      ? `${protocol}//${ident.codigo}.${host}`
      : `${protocol}//${host}/checkout/${ident.codigo}`;
  }

  const base = (dominio ?? dominioCanonico()).replace(/^\.+|\.+$/g, '');
  return ehSubdominio
    ? `https://${ident.codigo}.${base}`
    : `https://${base}/checkout/${ident.codigo}`;
}
